/**
 * The promo editor: texts per language, the colours with a live preview and a
 * readability warning, the https link, the status and dates, and which cards.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

const LANGS = ['en', 'fr', 'es'];
const MESSAGE_MAX = 300;

/** Relative luminance, so we can WARN (never block) on a hard-to-read button. */
const readable = (bg, fg) => {
    const lum = (hex) => {
        const v = String(hex || '').replace('#', '');
        if (v.length !== 6) return 1;
        const parts = [0, 2, 4].map((i) => {
            const c = parseInt(v.slice(i, i + 2), 16) / 255;
            return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
        });
        return 0.2126 * parts[0] + 0.7152 * parts[1] + 0.0722 * parts[2];
    };
    const a = lum(bg); const b = lum(fg);
    const ratio = (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
    return { ratio, ok: ratio >= 4.5 };
};

const blank = {
    name: '', link: '', bg_color: '#2563eb', text_color: '#ffffff',
    open_in: 'same', status: 'draft', target: 'org',
    button_text: {}, headline: {}, message: {}, continue_text: {},
    card_ids: [], location_ids: [], use_as_screen: true,
    use_as_button: false, step: '',
    starts_at: '', ends_at: '',
};

export const PromoEditor = ({ promo, orgId, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [row, setRow] = useState({ ...blank, ...promo });
    const [lang, setLang] = useState('en');
    const [cards, setCards] = useState([]);
    const [steps, setSteps] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get('/promos/cards', { params: { organization_id: orgId } })
            .then((r) => setCards(r.data.cards || []))
            .catch(() => setCards([]));
    }, [orgId]);

    useEffect(() => {
        api.get('/promos/steps').then((r) => setSteps(r.data.steps || []))
            .catch(() => setSteps([]));
    }, []);

    const set = (k, v) => setRow((p) => ({ ...p, [k]: v }));
    const setText = (field, value) => setRow((p) => ({
        ...p, [field]: { ...(p[field] || {}), [lang]: value },
    }));

    const contrast = useMemo(() => readable(row.bg_color, row.text_color),
        [row.bg_color, row.text_color]);
    const linkBad = row.link && !/^https:\/\/\S+$/i.test(row.link.trim());

    const save = async () => {
        if (!row.name.trim()) {
            toast.error(t('promo.needName', 'Give the promo a name'));
            return;
        }
        if (linkBad) {
            toast.error(t('promo.linkHttps', 'The link must start with https://'));
            return;
        }
        setBusy(true);
        try {
            const body = { ...row, organization_id: orgId };
            if (promo?.id) await api.put(`/promos/${promo.id}`, body);
            else await api.post('/promos', body);
            toast.success(t('promo.saved', 'Promo saved'));
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('promo.saveFailed', 'Could not save the promo'));
        } finally { setBusy(false); }
    };

    const toggleCard = (cardId) => setRow((p) => {
        const list = new Set(p.card_ids || []);
        if (list.has(cardId)) list.delete(cardId); else list.add(cardId);
        return { ...p, card_ids: [...list] };
    });

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="promo-editor">
                <DialogHeader>
                    <DialogTitle>
                        {promo?.id ? t('promo.editTitle', 'Edit promo') : t('promo.newTitle', 'New promo')}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5">
                    <div>
                        <Label>{t('promo.name', 'Name')}</Label>
                        <Input value={row.name} onChange={(e) => set('name', e.target.value)}
                               data-testid="promo-name" />
                    </div>

                    <div className="flex gap-1">
                        {LANGS.map((l) => (
                            <Button key={l} size="sm" type="button"
                                    variant={lang === l ? 'default' : 'outline'}
                                    onClick={() => setLang(l)} data-testid={`promo-lang-${l}`}>
                                {l.toUpperCase()}
                            </Button>
                        ))}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <Label>{t('promo.headline', 'Headline')}</Label>
                            <Input value={row.headline?.[lang] || ''}
                                   onChange={(e) => setText('headline', e.target.value)}
                                   placeholder={t('promo.headlineHint', 'Welcome to …')}
                                   data-testid="promo-headline" />
                        </div>
                        <div>
                            <Label>{t('promo.continueText', 'Continue button text')}</Label>
                            <Input value={row.continue_text?.[lang] || ''}
                                   onChange={(e) => setText('continue_text', e.target.value)}
                                   placeholder="Continue" data-testid="promo-continue-text" />
                        </div>
                    </div>

                    <div>
                        <Label>
                            {t('promo.message', 'Message')}{' '}
                            <span className="text-xs text-muted-foreground">
                                {(row.message?.[lang] || '').length}/{MESSAGE_MAX}
                            </span>
                        </Label>
                        <Textarea rows={3} maxLength={MESSAGE_MAX}
                                  value={row.message?.[lang] || ''}
                                  onChange={(e) => setText('message', e.target.value)}
                                  data-testid="promo-message" />
                    </div>

                    <div>
                        <Label>{t('promo.buttonText', 'Button text (for the Review Shield button, later)')}</Label>
                        <Input value={row.button_text?.[lang] || ''}
                               onChange={(e) => setText('button_text', e.target.value)}
                               data-testid="promo-button-text" />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <Label>{t('promo.link', 'Link (https only)')}</Label>
                            <Input value={row.link || ''} onChange={(e) => set('link', e.target.value)}
                                   placeholder="https://" data-testid="promo-link" />
                            {linkBad && (
                                <p className="text-xs text-rose-400 mt-1" data-testid="promo-link-error">
                                    {t('promo.linkHttps', 'The link must start with https://')}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label>{t('promo.openIn', 'Opens in')}</Label>
                            <select value={row.open_in} onChange={(e) => set('open_in', e.target.value)}
                                    data-testid="promo-open-in"
                                    className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                                <option value="same">{t('promo.openSame', 'Same tab')}</option>
                                <option value="new">{t('promo.openNew', 'New tab')}</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="flex items-end gap-3">
                            <div>
                                <Label>{t('promo.bgColor', 'Button colour')}</Label>
                                <input type="color" value={row.bg_color}
                                       onChange={(e) => set('bg_color', e.target.value)}
                                       data-testid="promo-bg-color"
                                       className="h-9 w-16 rounded-md border bg-transparent" />
                            </div>
                            <div>
                                <Label>{t('promo.textColor', 'Text colour')}</Label>
                                <input type="color" value={row.text_color}
                                       onChange={(e) => set('text_color', e.target.value)}
                                       data-testid="promo-text-color"
                                       className="h-9 w-16 rounded-md border bg-transparent" />
                            </div>
                        </div>
                        <div>
                            <Label>{t('promo.preview', 'Live preview')}</Label>
                            <div className="rounded-full px-5 py-3 text-center font-semibold"
                                 style={{ background: row.bg_color, color: row.text_color }}
                                 data-testid="promo-preview">
                                {row.continue_text?.[lang] || t('promo.continue', 'Continue')}
                            </div>
                            {!contrast.ok && (
                                <p className="text-xs text-amber-400 mt-1" data-testid="promo-contrast-warning">
                                    {t('promo.contrastWarning',
                                       'This text may be hard to read on that colour.')}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                            <Label>{t('promo.status.label', 'Status')}</Label>
                            <select value={row.status} onChange={(e) => set('status', e.target.value)}
                                    data-testid="promo-status"
                                    className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                                <option value="draft">{t('promo.status.draft', 'Draft')}</option>
                                <option value="on">{t('promo.status.on', 'On')}</option>
                                <option value="off">{t('promo.status.off', 'Off')}</option>
                            </select>
                        </div>
                        <div>
                            <Label>{t('promo.startsAt', 'Starts')}</Label>
                            <Input type="datetime-local" data-testid="promo-starts-at"
                                   value={(row.starts_at || '').slice(0, 16)}
                                   onChange={(e) => set('starts_at', e.target.value
                                       ? new Date(e.target.value).toISOString() : '')} />
                        </div>
                        <div>
                            <Label>{t('promo.endsAt', 'Ends')}</Label>
                            <Input type="datetime-local" data-testid="promo-ends-at"
                                   value={(row.ends_at || '').slice(0, 16)}
                                   onChange={(e) => set('ends_at', e.target.value
                                       ? new Date(e.target.value).toISOString() : '')} />
                        </div>
                    </div>

                    <div>
                        <Label>{t('promo.which', 'Which cards')}</Label>
                        <select value={row.target} onChange={(e) => set('target', e.target.value)}
                                data-testid="promo-target"
                                className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                            <option value="org">{t('promo.targetAll', 'All cards of the organization')}</option>
                            <option value="selection">{t('promo.targetSelection', 'A selection of cards')}</option>
                        </select>
                        {row.target === 'selection' && (
                            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border p-2 space-y-1"
                                 data-testid="promo-card-list">
                                {cards.map((c) => (
                                    <label key={c.card_id} className="flex items-center gap-2 text-sm">
                                        <input type="checkbox"
                                               checked={(row.card_ids || []).includes(c.card_id)}
                                               onChange={() => toggleCard(c.card_id)}
                                               data-testid={`promo-card-${c.card_id}`} />
                                        <span>{c.card_id}{c.name ? ` — ${c.name}` : ''}</span>
                                    </label>
                                ))}
                                {cards.length === 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        {t('promo.noCards', 'No cards in this organization.')}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border p-3 space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!row.use_as_button}
                                   onChange={(e) => set('use_as_button', e.target.checked)}
                                   data-testid="promo-use-as-button" />
                            <span>{t('promo.useAsButton', 'Use as a button inside the Review Shield flow')}</span>
                        </label>
                        {row.use_as_button && (
                            <div>
                                <Label>{t('promo.step', 'On which screen')}</Label>
                                <select value={row.step || ''} onChange={(e) => set('step', e.target.value)}
                                        data-testid="promo-step"
                                        className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                                    <option value="">{t('promo.stepPick', 'Choose a screen…')}</option>
                                    {steps.map((st) => (
                                        <option key={st} value={st}>{t(`promo.steps.${st}`, st)}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    {t('promo.stepNote',
                                       'The button is added below the existing buttons of that screen. Two per screen at most, and it always opens in a new tab.')}
                                </p>
                            </div>
                        )}
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!row.use_as_screen}
                               onChange={(e) => set('use_as_screen', e.target.checked)}
                               data-testid="promo-use-as-screen" />
                        <span>{t('promo.useAsScreen', 'Use as the promo screen (instead of the card\'s normal flow)')}</span>
                    </label>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="promo-cancel">
                        {t('promo.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={save} disabled={busy} data-testid="promo-save">
                        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        {t('promo.save', 'Save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
